import React, { useCallback, useEffect } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Close, Plus } from '@lyearn/icons';
import { Node, useNodeStatesContext, useReactNodeView } from '@lyearn/lygo-core';
import { BaseTab, BaseTabs, Box, Divider, Stack, Tooltip } from '@lyearn/ui';

import { findNodeInDescendants, insertNode, selectionType, setNodeAttrs } from '../../../utils';
import { generateNewBlockId } from '../../../utils/menus';
import TabLabel from './components/TabLabel';
import { TAB_CLASSES, TABS_PROPS } from './consts';

const TabsComponent: React.FC = () => {
  const { t } = useTranslation();
  const { node, view, editableComponentRef, getPos } = useReactNodeView();
  const { setNodeState, getNodeState } = useNodeStatesContext();

  //@ts-ignore
  const tabPanelNodes = node?.content.content;

  //@ts-ignore
  const startPosition = getPos() ?? 0;

  const nodeId = node?.attrs.id;

  const activeTabId = getNodeState?.(nodeId)?.activeTabId;

  const onAddTabClick = useCallback(() => {
    if (node && view) {
      const id = generateNewBlockId();
      const newTabPanelNode = view.state.schema.nodes.tab_panel.create(
        { id },
        view.state.schema.nodes.tab.create(undefined, view.state.schema.nodes.paragraph.create()),
      );

      insertNode(view, selectionType.NODE, newTabPanelNode, startPosition + node.nodeSize - 1);

      //set focus to new tab
      setNodeState?.(nodeId, { activeTabId: id });
    }
  }, [node, nodeId, setNodeState, startPosition, view]);

  const onRemoveTabClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();

      const id = e.currentTarget?.getAttribute('data-id');
      if (view && node && id) {
        const tabPanelNode = findNodeInDescendants(node, (node) => node.attrs.id === id);
        if (!tabPanelNode || !tabPanelNode?.foundNode) {
          return;
        }
        const { pos, node: tabnode } = tabPanelNode?.foundNode;

        const posStart = tabPanelNode.isFirstDescendant ? pos : pos + 1;
        const posEnd = tabPanelNode.isFirstDescendant
          ? posStart + tabnode.nodeSize + 1
          : posStart + tabnode.nodeSize;
        const deleteTr = view.state.tr.delete(startPosition + posStart, startPosition + posEnd);
        view.dispatch(deleteTr);

        //current active tab is removed
        if (activeTabId === id) {
          tabPanelNodes.some((tpNode: Node) => {
            if (tpNode.attrs.id !== id) {
              setNodeState?.(nodeId, { activeTabId: tpNode.attrs.id });

              return true;
            }

            return false;
          });
        }
      }
    },
    [activeTabId, node, nodeId, setNodeState, startPosition, tabPanelNodes, view],
  );

  const handleChange = useCallback(
    (_event: React.ChangeEvent<{}>, newValue: number) => {
      if (newValue === activeTabId || !node || !view) {
        return;
      }
      setNodeState?.(nodeId, { activeTabId: newValue });
    },
    [activeTabId, node, nodeId, setNodeState, view],
  );

  useEffect(() => {
    if (!activeTabId) {
      const firstTabPanelId = tabPanelNodes[0].attrs.id;
      setNodeState?.(nodeId, { activeTabId: firstTabPanelId });
    }
  }, [activeTabId, nodeId, setNodeState, tabPanelNodes]);

  const onTabPanelNameChange = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newName = event.target.value;
      const id = event.target?.id;

      if (node && view && id) {
        const tabPanelNode = findNodeInDescendants(node, (node) => node.attrs.id === id);
        if (!tabPanelNode || !tabPanelNode?.foundNode) {
          return;
        }
        const pos = startPosition + tabPanelNode.foundNode?.pos + 1;

        setNodeAttrs(view, pos, { name: newName, id });
      }
    },
    [node, startPosition, view],
  );

  const addNewParagraphNodeAtEnd = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      if (node && view && activeTabId) {
        const tabViewDescendant = findNodeInDescendants(
          node,
          (node) => node.attrs.id === activeTabId,
        );
        if (!tabViewDescendant || !tabViewDescendant?.foundNode) {
          return;
        }
        const { pos, node: tabPanelnode } = tabViewDescendant?.foundNode;

        const tabNode = tabPanelnode?.firstChild;
        const newParaTagPos = startPosition + pos + (tabNode?.nodeSize ?? 0) + 1;

        if (!tabNode || !newParaTagPos || !tabNode.lastChild) {
          return;
        }

        if (
          ![view.state.schema.nodes?.paragraph, view.state.schema.nodes?.heading].includes(
            tabNode.lastChild.type,
          )
        ) {
          insertNode(
            view,
            selectionType.NODE,
            view.state.schema.nodes.paragraph.create(),
            newParaTagPos,
          );
        }
      }
    },
    [activeTabId, node, startPosition, view],
  );

  return (
    <Stack
      className="node-container tabsContainer -mx-24 my-20 rounded-16 border border-divider xs:mx-0"
      direction="col">
      <Stack
        alignItems="center"
        className="pt-8 pe-4 ps-20"
        contentEditable={false}
        justifyContent="between">
        <BaseTabs {...TABS_PROPS} value={activeTabId} variant="scrollable" onChange={handleChange}>
          {tabPanelNodes.map((tabPanelNode: Node) => {
            const { id, name } = tabPanelNode.attrs;

            return (
              <BaseTab
                key={id}
                className="tabview-tab"
                classes={TAB_CLASSES}
                contentEditable={false}
                label={
                  <>
                    <Tooltip
                      disableFocusListener={!name || (name && name.length < 16)}
                      disableHoverListener={!name || (name && name.length < 16)}
                      title={name || t('Lygo:Menu.tabs.UntitledTab')}>
                      <TabLabel
                        defaultValue={name}
                        id={id}
                        isActive={activeTabId === id}
                        onBlur={onTabPanelNameChange}
                      />
                    </Tooltip>
                    <Tooltip title={t('Lygo:Menu.tabs.DeleteTab') || 'Delete Tab'}>
                      <Box
                        className="absolute top-14 end-10"
                        data-id={id}
                        role="button"
                        onClick={onRemoveTabClick}>
                        <Close height={20} width={20} />
                      </Box>
                    </Tooltip>
                  </>
                }
                value={id}
              />
            );
          })}
          <Tooltip title={t('Lygo:Menu.tabs.AddTab') || 'Add Tab'}>
            <Box
              className="transform cursor-pointer px-16 ms-auto "
              role="button"
              onClick={onAddTabClick}>
              <Plus height={20} width={20} />
            </Box>
          </Tooltip>
        </BaseTabs>
      </Stack>
      <Box contentEditable={false}>
        <Divider />
      </Box>
      <div ref={editableComponentRef} className="px-24 pt-24" />
      <Box contentEditable={false}>
        <button
          className="h-28 w-full cursor-default opacity-0"
          onClick={addNewParagraphNodeAtEnd}
        />
      </Box>
    </Stack>
  );
};

export default TabsComponent;
